import { TestReportItem } from './TestReports.query';
import { format, parseISO } from 'date-fns';
import StatusDot, {
  StatusStyle,
} from '../../../../components/Status/StatusDot';
import React from 'react';
import { Column } from '../../../../hooks/useTablePages';
import { Flex, Text } from 'rebass';
import {
  waterSupplierApprovalStatusLabel,
  waterSupplierApprovalStatusStyle,
} from '../../../../components/BackflowTest/waterSupplierApprovalStatus';
import { flagOptions } from '../../../../components/BackflowTest/BackflowTestFlagsPicker';
import Icon from '../../../../components/Icon/Icon';

const columns: Column[] = [
  {
    header: 'Test',
    id: 'jobId',
    accessor: (row: TestReportItem) => {
      return (
        <Flex flexDirection="row" alignItems="center" sx={{ gap: 1 }}>
          {row.jobId}
          {!!row?.job?.attachments?.length && (
            <Icon title={'Has photos'} icon="image" size="16" />
          )}
        </Flex>
      );
    },
    stringAccessor: (row) => row.jobId,
    width: 100,
  },
  {
    header: 'Approval',
    id: 'waterSupplierStatus',
    accessor: (row: TestReportItem) => {
      const style: StatusStyle = waterSupplierApprovalStatusStyle(
        row.waterSupplierStatus
      );
      return (
        <Flex>
          <StatusDot status={style} mr="1" />
          <Text variant="body2">
            {waterSupplierApprovalStatusLabel(row.waterSupplierStatus)}
          </Text>
        </Flex>
      );
    },
    stringAccessor: (row: TestReportItem): string => {
      return waterSupplierApprovalStatusLabel(row.waterSupplierStatus);
    },
    width: 150,
  },
  // {
  //  header: 'Result',
  //   id: 'finalResult',
  //   accessor: (row: TestReportItem) => {
  //     if (!row.finalResult || row.finalResult === 'FAIL') {
  //       return <StatusDot title="Fail" status="error" />
  //     }
  //
  //     if (row.initialResult === 'FAIL') {
  //       return (
  //         <Flex title="Fail then Pass">
  //           <StatusDot status="error" />
  //           <StatusDot status="success" />
  //         </Flex>
  //       )
  //     }
  //
  //     return <StatusDot title="Pass" status="success" />
  //   },
  //   stringAccessor: (row: TestReportItem): string => {
  //     return constantToLabel(row.finalResult)
  //   },
  //   width: 80,
  // },
  {
    header: 'Flags',
    id: 'flags',
    sortable: false,
    accessor: (row: TestReportItem) => {
      const matches = flagOptions.filter((option) => {
        for (const field in option.filter) {
          if (row[field as keyof TestReportItem] !== option.filter[field]) {
            return false;
          }
        }
        return true;
      });

      const toDisplay = matches.filter(
        (option) =>
          option.value !== 'pass' ||
          !matches.find((opt) => opt.value === 'clear')
      );

      return (
        <Flex flexDirection="row">
          {toDisplay.map((option) => (
            <Flex title={option.label} flexDirection="row" key={option.value}>
              <StatusDot color={option.color} />
            </Flex>
          ))}
        </Flex>
      );
    },
    stringAccessor: (row: TestReportItem): string => {
      const matches = flagOptions.filter((option) => {
        for (const field in option.filter) {
          if (row[field as keyof TestReportItem] !== option.filter[field]) {
            return false;
          }
        }
        return true;
      });

      const toDisplay = matches.filter(
        (option) =>
          option.value !== 'pass' ||
          !matches.find((opt) => opt.value === 'clear')
      );

      return toDisplay.map((option) => option.label).join(', ');
    },
    width: 80,
  },
  {
    header: 'Test Anniv.',
    id: 'backflowAssembly.testAnniversary',
    accessor: (row: TestReportItem) => {
      const testAnniversary = row.backflowAssembly?.testAnniversary;
      if (!testAnniversary || testAnniversary.startsWith('00')) {
        return '';
      }
      return format(parseISO(testAnniversary), 'MMM do');
    },
    width: 150,
  },
  {
    header: 'Test Date',
    id: 'dateTested',
    accessor: (row: TestReportItem) => {
      if (!row.dateTested || row.dateTested.startsWith('00')) {
        return '';
      }
      return format(parseISO(row.dateTested), 'M/d/yyyy');
    },
    width: 100,
  },
  {
    header: 'Customer',
    id: 'backflowAssembly.waterService.serviceAddresses.customer.profile.name',
    accessor: (row: TestReportItem) => {
      if (!row.backflowAssembly?.waterService?.serviceAddresses?.length) {
        return '';
      }
      return (
        row.backflowAssembly.waterService.serviceAddresses.find(
          (serviceAddress) => !!serviceAddress.customer
        )?.customer?.profile?.name ?? ''
      );
    },
    width: 170,
  },
  {
    header: 'Testing Company',
    accessor: 'testingCompanyName',
    width: 220,
  },
  {
    header: 'Tester',
    accessor: 'testerName',
    width: 170,
  },
  {
    header: 'Submitted',
    id: 'testingCompanySubmitted',
    accessor: (row: TestReportItem) => {
      if (!row.testingCompanySubmitted) {
        return '';
      }
      return format(row.testingCompanySubmitted, 'M/d/yyyy');
    },
    width: 100,
  },
  {
    header: 'Service',
    accessor: 'backflowAssembly.waterService.siteNumber',
    width: 120,
  },
  {
    header: 'TRAC',
    accessor: 'backflowAssembly.waterService.pin',
    width: 80,
  },
  {
    header: 'Address',
    id: 'job.address.street1',
    accessor: (row: TestReportItem) =>
      row.job.address.street1 +
      (row.job.address.street2 ? ', ' + row.job.address.street2 : ''),
    width: 250,
  },
  {
    header: 'City',
    accessor: 'job.address.city',
    width: 120,
  },
  {
    header: 'State',
    accessor: 'job.address.state',
    width: 80,
  },
  {
    header: 'Zip',
    id: 'job.address.zip',
    accessor: (row: TestReportItem) =>
      row.job.address.zip +
      (row.job.address.zipExt ? '-' + row.job.address.zipExt : ''),
    width: 120,
  },
  {
    header: 'Make',
    accessor: 'backflowAssembly.model.backflowAssemblyMake.name',
    width: 120,
  },
  {
    header: 'Model',
    accessor: 'backflowAssembly.model.name',
    width: 110,
  },
  {
    header: 'Serial',
    accessor: 'backflowAssembly.serialNumber',
    width: 110,
  },
  {
    header: 'Size',
    id: 'size',
    accessor: (row: TestReportItem): string =>
      row.backflowAssembly?.size ? row.backflowAssembly.size + '"' : '',
    width: 80,
  },
  {
    header: 'Type',
    accessor: 'backflowAssembly.type',
    width: 80,
  },
  {
    header: 'Location',
    accessor: 'backflowAssembly.location',
    width: 350,
  },
];

export default columns;
